import { useContext, useEffect, useMemo, useRef } from 'react';
import type { Room } from '@studio/models/room';
import type { Agent } from '@studio/models/agent';
import type { State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import {
  StatusEgressReason as R,
  StatusEgressState as S
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import { broker } from '#~/agent/broker.js';
import { createAlertTracker } from '#~/analytics/domains/alert-lifecycle.js';
import { useAnalytics } from '#~/analytics/hooks/use-analytics.js';
import {
  raise,
  resolve,
  subscribeIncidents,
  supersede,
  type IncidentKey
} from '#~/notifications/incidents.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { Context } from '#~/room/provider/context.js';
import { useMultiProxyStateQuery } from '#~/statequery/hook.js';

const selectDiskSpace = (state: State | null | undefined) => ({
  blocked: state?.status?.encoder?.diskRecordingBlocked,
  free: state?.status?.encoder?.diskFreeSpace,
  deviceIds: Object.keys(state?.control?.device ?? {}),
  recordings: Object.fromEntries(
    Object.entries(state?.status?.device ?? {}).map(([id, device]) => [
      id,
      device.egress?.recordingStatus
    ])
  )
});

/** Read the encoder's gate, including its recovery margin; never calculate it in Studio. */
export const useRecordingDiskSpace = (selectedRoomId?: Room['id'] | null) => {
  // App-level recording dialogs supply their studio because they sit outside
  // the room provider. Room-mounted consumers use the live context instead.
  const roomId = useContext(Context) ?? selectedRoomId;
  const { enabled, loading } = useFeatureFlag('alert_n5_enabled', { defaultValue: true });
  const snapshots = useMultiProxyStateQuery<ReturnType<typeof selectDiskSpace>>(selectDiskSpace);
  const bridges = useMemo(
    () =>
      Object.entries(snapshots).flatMap(([id, snapshot]) => {
        const proxy = broker.acquire(id as Agent['id']);
        return proxy && roomId && proxy.roomId === roomId
          ? [{ ...snapshot, id, name: proxy.name ?? 'Bridge' }]
          : [];
      }),
    [snapshots, roomId]
  );
  const allowed = !loading && enabled === true;
  const blockedReasonByDevice = useMemo(
    () =>
      Object.fromEntries(
        bridges
          .filter((bridge) => allowed && bridge.blocked === true)
          .flatMap((bridge) =>
            bridge.deviceIds.map((id) => [id, `${bridge.name} is almost out of space`])
          )
      ),
    [bridges, allowed]
  );
  return { bridges, allowed, blockedReasonByDevice };
};

/** One N5 incident per Bridge, observed by every collaborator, including viewers. */
export const useRecordingDiskNotices = (): void => {
  const roomId = useContext(Context);
  const { bridges, allowed } = useRecordingDiskSpace();
  const { track } = useAnalytics();
  const tracker = useMemo(() => createAlertTracker(track), [track]);
  const episodes = useRef(
    new Map<string, { key: IncidentKey; consumed: boolean; recordings: Set<string> }>()
  );
  const active = useRef(new Map<string, Set<string>>());

  useEffect(() => {
    const unsubscribe = subscribeIncidents((event) => {
      const { incident } = event;
      if (incident.alert !== 'n5' || incident.audience !== roomId || !incident.entity) return;
      if (!episodes.current.has(incident.entity)) return;
      const showing = {
        alert_id: 'n5' as const,
        alert_variant:
          incident.variant === 'recording' ? ('recording' as const) : ('not_recording' as const),
        showing_id: incident.showingId,
        studio_id: roomId!,
        agent_id: incident.entity
      };
      if (event.type === 'shown') tracker.trackAlertShown(showing);
      else if (event.type === 'closed') tracker.trackAlertClosed(showing, event.reason);
    });
    return () => {
      for (const episode of episodes.current.values()) supersede(episode.key);
      unsubscribe();
      episodes.current.clear();
      active.current.clear();
    };
  }, [roomId, tracker]);

  useEffect(() => {
    if (!roomId) return;
    for (const bridge of bridges) {
      const statuses = Object.entries(bridge.recordings);
      const tracked = active.current.get(bridge.id) ?? new Set<string>();
      const currentRecordings = new Set(tracked);
      for (const [id, status] of statuses) {
        if (status?.state === S.EGRESS_STATE_ACTIVE) currentRecordings.add(id);
      }
      // Recording history ends on a terminal normal stop even while metrics are
      // unknown. A pending disk incident retains its own affected camera set.
      active.current.set(
        bridge.id,
        new Set(
          statuses
            .filter(
              ([id, status]) =>
                status?.state === S.EGRESS_STATE_ACTIVE ||
                (tracked.has(id) &&
                  (status?.state === S.EGRESS_STATE_STOPPING ||
                    status?.state === S.EGRESS_STATE_FAILED))
            )
            .map(([id]) => id)
        )
      );
      let episode = episodes.current.get(bridge.id);
      // Absence is neither a denial nor proof of recovery. Retained recording
      // reasons do not override the encoder's explicit recovery signal.
      if (bridge.blocked === false) {
        if (episode) resolve(episode.key);
        episodes.current.delete(bridge.id);
        continue;
      }
      if (!allowed && episode) supersede(episode.key);
      if (bridge.blocked !== true) continue;
      if (!episode) {
        episode = {
          key: { alert: 'n5', entity: bridge.id, audience: roomId },
          consumed: false,
          recordings: currentRecordings
        };
        episodes.current.set(bridge.id, episode);
      }
      if (!allowed) {
        // Loading/off showings are skipped, never queued for flag resolution.
        episode.consumed = true;
        continue;
      }
      if (episode.consumed) continue;
      // Only the encoder can confirm a stop. A low metric while recording
      // must not claim files have stopped before the disk-full outcome arrives.
      const diskStop = statuses.some(
        ([id, status]) =>
          episode.recordings.has(id) &&
          status?.state === S.EGRESS_STATE_INACTIVE &&
          status.reason === R.EGRESS_REASON_DISK_FULL
      );
      if (episode.recordings.size > 0 && !diskStop) continue;
      if (bridge.free === undefined || bridge.free < 0) continue;
      const variant = episode.recordings.size > 0 ? 'recording' : 'not_recording';
      episode.consumed = true;
      raise(
        episode.key,
        {
          title: variant === 'recording' ? 'Recording stopped' : "Can't record right now",
          message: `${bridge.name} is almost out of space (${(bridge.free / 1e9).toFixed(1)} GB left). Free up space on it to ${variant === 'recording' ? 'record again' : 'record'}.`,
          severity: 'error'
        },
        { variant }
      );
    }
  }, [bridges, allowed, roomId]);
};
